import type { ContentHref, ReplacesRelation } from '@generalTypes/apiTypes';
import type { ContentRelationsMap } from '@generalTypes/generalTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import {
  selectApiWithPendingChangesRelationsToAndFromMap,
  selectBaselineRelationsToAndFromMap,
} from '@newStore/documentApi/documentApiSelectors';
import {
  selectExternalContent,
  selectExternalContentRelationsMap,
} from '@newStore/externalData/externalDataSelectors';
import { createTypedSelector } from '@newStore/genericHelpers';
import { getExternalGoalOrItem } from '@newStore/llinkid/llinkidSelectors';
import type {
  AsideChangeMessageSelector,
  EditAsidePreviousVersionItems,
} from '@nodeTypeConfig/configTypes';

const getReplacesRelations = (
  relationsMap: ContentRelationsMap,
  href: ContentHref,
  config: EditAsidePreviousVersionItems,
) => {
  return relationsMap.from[href]?.filter((relation) =>
    config.options.relationTypes.some((z) => z === relation.relationtype),
  );
};

export const selectReplacesRelations = createTypedSelector(
  [
    (state: RootState) => state.documentUI.currentEditingNode as ContentHref,
    (state: RootState) => selectApiWithPendingChangesRelationsToAndFromMap(state),
    (state: RootState, config: EditAsidePreviousVersionItems) => config,
  ],
  (
    currentEditNode: ContentHref,
    relationsMap: ContentRelationsMap,
    config: EditAsidePreviousVersionItems,
  ) => {
    return getReplacesRelations(relationsMap, currentEditNode, config) as ReplacesRelation[];
  },
);

export const selectChangeMessageForPreviousVersionItems: AsideChangeMessageSelector<EditAsidePreviousVersionItems> =
  createTypedSelector(
    [
      (state) => selectExternalContent(state),
      (state) => selectExternalContentRelationsMap(state),
      (state) => selectApiWithPendingChangesRelationsToAndFromMap(state),
      (state) => selectBaselineRelationsToAndFromMap(state),
      (state, href: ContentHref) => href,
      (state, href, config: EditAsidePreviousVersionItems) => config,
    ],
    (
      externalData,
      externalDataRelationsMap,
      newRelationsMap,
      oldRelationsMap,
      href,
      config,
    ): string | null => {
      const newRelations = getReplacesRelations(newRelationsMap, href, config);
      const oldRelations = getReplacesRelations(oldRelationsMap, href, config);

      const newValues = newRelations
        ?.map((rel) => externalData[rel.to.href])
        .filter((value) => value !== undefined);

      const oldValues = oldRelations
        ?.map((rel) => externalData[rel.to.href])
        .filter((value) => value !== undefined);

      const oldValuesMap = new Map(
        oldValues?.map((goal) => [
          goal.$$meta.permalink,
          getExternalGoalOrItem(externalDataRelationsMap, externalData, goal.$$meta.permalink),
        ]),
      );
      const newValuesMap = new Map(
        newValues?.map((goal) => [
          goal.$$meta.permalink,
          getExternalGoalOrItem(externalDataRelationsMap, externalData, goal.$$meta.permalink),
        ]),
      );

      const allKeys = new Set([...oldValuesMap.keys(), ...newValuesMap.keys()]);

      const changes = [...allKeys]
        .map((key) => {
          const oldGoal = oldValuesMap.get(key);
          const newGoal = newValuesMap.get(key);

          if (oldGoal && !newGoal) {
            return `<del>${oldGoal.identifier ? `${oldGoal.identifier} - ` : ''}${
              oldGoal.descriptionText
            } (v${oldGoal.version})</del>`;
          }
          if (newGoal && !oldGoal) {
            return `<ins>${newGoal.identifier ? `${newGoal.identifier} - ` : ''}${
              newGoal.descriptionText
            } (v${newGoal.version})</ins>`;
          }
          return null;
        })
        .filter((change) => change !== null);

      if (changes.length === 0) {
        return null;
      }

      return `<ul>${changes.map((change) => `<li>${change}</li>`).join('')}</ul>`;
    },
  );
